import React from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { 
  Users, Calendar, Settings, ClipboardCheck, Shuffle, 
  Clock, Award, Trophy, DollarSign, BarChart3, FileText, 
  LogOut, Home, UserCheck, ClipboardList, ShieldCheck
} from "lucide-react";

// Admin Pages
import ContestantsPage from "./ContestantsPage";
import MembersPage from "./MembersPage";
import SetupPage from "./SetupPage";
import RegistrationsPage from "./RegistrationsPage";
import CheckInPage from "./CheckInPage";
import DrawsPage from "./DrawsPage";
import InputTimesPage from "./InputTimesPage";
import PayoutPointsPage from "./PayoutPointsPage";
import FinalResultsPage from "./FinalResultsPage";
import WinningsPage from "./WinningsPage";
import PointsPage from "./PointsPage";
import YearEndPage from "./YearEndPage";
import FinancialPage from "./FinancialPage";
import AdminHomePage from "./AdminHomePage";
import PendingAdminsPage from "./PendingAdminsPage";

const navItems = [
  { path: "", icon: Home, label: "Dashboard" },
  { path: "contestants", icon: Users, label: "Contestants" },
  { path: "members", icon: UserCheck, label: "Members" },
  { path: "setup", icon: Settings, label: "Setup" },
  { path: "registrations", icon: ClipboardList, label: "Registrations" },
  { path: "checkin", icon: ClipboardCheck, label: "Check-In" },
  { path: "draws", icon: Shuffle, label: "Draws" },
  { path: "times", icon: Clock, label: "Input Times" },
  { path: "payouts", icon: Award, label: "Payout & Points" },
  { path: "results", icon: Trophy, label: "Final Results" },
  { path: "winnings", icon: DollarSign, label: "Winnings" },
  { path: "points", icon: BarChart3, label: "Points" },
  { path: "yearend", icon: Trophy, label: "Year End" },
  { path: "financial", icon: FileText, label: "Financial" },
  { path: "approvals", icon: ShieldCheck, label: "Approvals" },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path) => {
    const currentPath = location.pathname.replace("/admin", "").replace(/^\//, "");
    return currentPath === path;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-primary/20 fixed left-0 top-0 bottom-0 flex flex-col" data-testid="admin-sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-primary/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <span className="font-heading font-bold text-primary-foreground">TTP</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-text-primary">TTP Play Day</h1>
              <p className="text-xs text-text-secondary">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={`/admin/${item.path}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-medium ${
                  isActive(item.path)
                    ? "bg-primary text-primary-foreground"
                    : "text-text-secondary hover:bg-secondary hover:text-text-primary"
                }`}
                data-testid={`nav-${item.path || 'home'}`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-primary/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
              <span className="font-bold text-text-primary">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-text-primary truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-text-secondary truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full border-gray-200 text-text-secondary hover:text-text-primary hover:bg-secondary"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6">
        <Routes>
          <Route index element={<AdminHomePage />} />
          <Route path="contestants" element={<ContestantsPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="setup" element={<SetupPage />} />
          <Route path="registrations" element={<RegistrationsPage />} />
          <Route path="checkin" element={<CheckInPage />} />
          <Route path="draws" element={<DrawsPage />} />
          <Route path="times" element={<InputTimesPage />} />
          <Route path="payouts" element={<PayoutPointsPage />} />
          <Route path="results" element={<FinalResultsPage />} />
          <Route path="winnings" element={<WinningsPage />} />
          <Route path="points" element={<PointsPage />} />
          <Route path="yearend" element={<YearEndPage />} />
          <Route path="financial" element={<FinancialPage />} />
          <Route path="approvals" element={<PendingAdminsPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
